import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faPencil, faBoxArchive, faRotateLeft, faHandHoldingDroplet,
    faTruckFast, faMagnifyingGlass, faXmark,
} from '@fortawesome/free-solid-svg-icons';

import { CategoryFilter, LinkButtons, sinceLabel, exactTime } from './stockShared';

/**
 * The stock list: everything we buy, grouped by shelf.
 *
 * WHY IT IS GROUPED BY CATEGORY AND NOT ONE FLAT TABLE. The warehouse thinks
 * in shelves — linen, amenities, chemicals, equipment — and a person hunting
 * for the right bleach is standing in front of the chemicals shelf, not
 * scanning an alphabetical list of ninety things. The category filter narrows
 * it; the headings keep the mental model even when it is not filtered.
 *
 * SEARCH IS THE PRIMARY CONTROL. With this many lines, typing three letters
 * beats every other way of finding something, so the box is full width and at
 * the top rather than tucked beside a filter row.
 *
 * AN ITEM SHOWS ITS OWN STATE. If something is already requested or already on
 * order, the card says so INSTEAD of offering a Request button. That is the
 * single guard that stops the same box being asked for by three people on the
 * same morning — and it is on the card, where the decision is made.
 */
export default function StockCatalog({
    items, categories, categoryId, onCategory, showArchived, onShowArchived,
    isManager, onRequest, onEditItem, onArchiveItem, onAddItem,
}) {
    const [q, setQ] = useState('');

    const visible = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return items.filter((i) => {
            if (!showArchived && !i.is_active) return false;
            if (categoryId && i.category_id !== categoryId) return false;
            if (!needle) return true;
            return `${i.name} ${i.category_name} ${i.note || ''}`.toLowerCase().includes(needle);
        });
    }, [items, q, categoryId, showArchived]);

    const grouped = useMemo(() => {
        const map = new Map();
        visible.forEach((i) => {
            if (!map.has(i.category_id)) {
                map.set(i.category_id, { name: i.category_name, sort: i.category_sort, items: [] });
            }
            map.get(i.category_id).items.push(i);
        });
        return [...map.entries()]
            .sort((a, b) => (a[1].sort - b[1].sort) || a[1].name.localeCompare(b[1].name))
            .map(([id, group]) => ({ id, ...group }));
    }, [visible]);

    return (
        <div className="stk__catalog">
            <div className="stk__searchBig">
                <FontAwesomeIcon icon={faMagnifyingGlass} className="stk__searchIcon" />
                <input
                    type="search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search stock — type a few letters"
                    aria-label="Search the stock list"
                />
                {q && (
                    <button
                        type="button"
                        className="stk__searchClear"
                        onClick={() => setQ('')}
                        aria-label="Clear the search"
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                )}
            </div>

            <CategoryFilter
                categories={categories}
                value={categoryId}
                onChange={onCategory}
                extra={isManager ? (
                    <>
                        <label className="stk__toggle">
                            <input
                                type="checkbox"
                                checked={showArchived}
                                onChange={(e) => onShowArchived(e.target.checked)}
                            />
                            Show archived
                        </label>
                        <button type="button" className="stk__btn stk__btn--primary" onClick={onAddItem}>
                            <FontAwesomeIcon icon={faPlus} /> Add item
                        </button>
                    </>
                ) : null}
            />

            {grouped.length === 0 ? (
                <div className="stk__empty">
                    <p className="stk__emptyTitle">
                        {q ? `Nothing matches “${q}”` : 'Nothing here yet'}
                    </p>
                    <p className="stk__emptyBody">
                        {isManager
                            ? 'Add the item once, with its buying links and how long delivery usually takes, and it can be requested from then on.'
                            : 'Ask a manager to add what you need to the stock list.'}
                    </p>
                    {isManager && (
                        <button type="button" className="stk__btn stk__btn--primary" onClick={onAddItem}>
                            <FontAwesomeIcon icon={faPlus} /> Add item
                        </button>
                    )}
                </div>
            ) : (
                grouped.map((group) => (
                    <section key={group.id} className="stk__section">
                        <div className="stk__sectionHead">
                            <h3 className="stk__sectionTitle">
                                {group.name}
                                <span className="stk__sectionCount">{group.items.length}</span>
                            </h3>
                        </div>
                        <div className="stk__grid">
                            {group.items.map((item) => (
                                <ItemCard
                                    key={item.id}
                                    item={item}
                                    isManager={isManager}
                                    onRequest={() => onRequest(item)}
                                    onEdit={() => onEditItem({ id: item.id })}
                                    onArchive={() => onArchiveItem(item, item.is_active)}
                                />
                            ))}
                        </div>
                    </section>
                ))
            )}
        </div>
    );
}

function ItemCard({ item, isManager, onRequest, onEdit, onArchive }) {
    const open = item.open_request_id ? item.open_request_status : null;

    return (
        <article className={`stk__card ${item.is_active ? '' : 'is-archived'}`}>
            <div className="stk__cardHead">
                <h4 className="stk__cardTitle">{item.name}</h4>
                {!item.is_active && <span className="stk__pill stk__pill--muted">Archived</span>}
            </div>

            <dl className="stk__facts">
                <div className="stk__fact">
                    <dt><FontAwesomeIcon icon={faTruckFast} /> Delivery</dt>
                    <dd className={item.delivery_estimate ? '' : 'stk__factMuted'}>
                        {item.delivery_estimate || 'not recorded'}
                    </dd>
                </div>
                <div className="stk__fact">
                    <dt>Last ordered</dt>
                    <dd
                        className={item.last_ordered_at ? '' : 'stk__factMuted'}
                        title={exactTime(item.last_ordered_at)}
                    >
                        {item.last_ordered_at ? sinceLabel(item.last_ordered_at) : 'never'}
                    </dd>
                </div>
                <div className="stk__fact">
                    <dt>Last received</dt>
                    <dd
                        className={item.last_delivered_at ? '' : 'stk__factMuted'}
                        title={exactTime(item.last_delivered_at)}
                    >
                        {item.last_delivered_at ? sinceLabel(item.last_delivered_at) : 'never'}
                    </dd>
                </div>
            </dl>

            {item.note ? <p className="stk__cardNote">{item.note}</p> : null}

            {isManager && <LinkButtons links={item.links} compact />}

            <div className="stk__cardFoot">
                {open === 'requested' ? (
                    <span className="stk__cardState stk__cardState--warn">Already requested</span>
                ) : open === 'ordered' ? (
                    <span className="stk__cardState stk__cardState--info">On the way</span>
                ) : item.is_active ? (
                    <button type="button" className="stk__btn stk__btn--primary stk__btn--block" onClick={onRequest}>
                        <FontAwesomeIcon icon={faHandHoldingDroplet} /> Request
                    </button>
                ) : (
                    <span className="stk__cardState stk__cardState--muted">Archived</span>
                )}

                {isManager && (
                    <div className="stk__cardActions">
                        <button type="button" className="stk__iconBtn" onClick={onEdit}>
                            <FontAwesomeIcon icon={faPencil} /> Edit
                        </button>
                        <button type="button" className="stk__iconBtn" onClick={onArchive}>
                            <FontAwesomeIcon icon={item.is_active ? faBoxArchive : faRotateLeft} />
                            {item.is_active ? ' Archive' : ' Restore'}
                        </button>
                    </div>
                )}
            </div>
        </article>
    );
}
